import { APP_STORAGE_PREFIX } from './local-storage';

/**
 * Marker key written once the legacy ag-grid localStorage migration has run.
 * Prevents the (cheap, but non-zero cost) scan from re-running on every page load.
 *
 * This migration, this marker, and the call to {@link migrateLegacyAgGridLocalStorageKeys}
 * can all be deleted once we're confident essentially all active users have loaded the
 * app at least once since it shipped (e.g. after a couple of releases).
 */
// eslint-disable-next-line @typescript-eslint/naming-convention
export const AG_GRID_MIGRATION_MARKER = `${APP_STORAGE_PREFIX}:migrations:ag-grid-namespace-v1`;

// eslint-disable-next-line @typescript-eslint/naming-convention
const AG_GRID_NAMESPACE = 'ag-grid';
// eslint-disable-next-line @typescript-eslint/naming-convention
const LEGACY_GROUP_STATE_SUFFIX = '-group-state';
// eslint-disable-next-line @typescript-eslint/naming-convention
const NEW_COLUMN_STATE_SUFFIX = 'column-state';
// eslint-disable-next-line @typescript-eslint/naming-convention
const NEW_GROUP_STATE_SUFFIX = 'group-state';

// Keys written by libraries we know about, which must never be touched by this migration
// even if they happen to not start with our prefix.
// eslint-disable-next-line @typescript-eslint/naming-convention
const IGNORED_PREFIXES = ['CognitoIdentityServiceProvider', 'amplify-', 'aws-amplify', 'aws.cognito'];

function buildNewColumnStateKey(heading: string): string {
	return `${APP_STORAGE_PREFIX}:${AG_GRID_NAMESPACE}:${heading}:${NEW_COLUMN_STATE_SUFFIX}`;
}

function buildNewGroupStateKey(heading: string): string {
	return `${APP_STORAGE_PREFIX}:${AG_GRID_NAMESPACE}:${heading}:${NEW_GROUP_STATE_SUFFIX}`;
}

/**
 * Both halves of the legacy pair are JSON arrays: column state comes from
 * ColumnApi.getColumnState() (ColumnState[]) and group state from
 * ColumnApi.getColumnGroupState() ({ groupId, open }[]). Note that group state is very
 * commonly an empty array, since most grids have no column groups - that still migrates.
 *
 * This is a cheap sanity check to avoid migrating a key that merely happens to have a
 * "-group-state" sibling by coincidence, e.g. a third-party cache entry shaped like
 * `{ value, expiration }`, which is an object and so is rejected here.
 */
function looksLikeGridState(rawValue: string): boolean {
	try {
		return Array.isArray(JSON.parse(rawValue));
	} catch {
		return false;
	}
}

/**
 * One-time migration that moves legacy, unprefixed ag-grid session-state keys
 * (written directly as `<heading>` and `<heading>-group-state`) into the namespaced
 * `vmol-cobra:ag-grid:<heading>:column-state` / `vmol-cobra:ag-grid:<heading>:group-state`
 * keys used by {@link setLocalStorageData}/{@link getLocalStorageData}.
 *
 * Only migrates keys found in matching pairs (a base key AND its `-group-state` sibling),
 * since that pairing is a strong signal the keys were written by our ag-grid code, without
 * needing to guess at what else might be present in localStorage. Keys that don't have a
 * matching pair, or don't look like grid state, are left untouched.
 */
export function migrateLegacyAgGridLocalStorageKeys(storage?: Storage): void {
	try {
		storage = storage ?? localStorage;
		runMigration(storage);
	} catch (error) {
		// This runs as an APP_INITIALIZER - never let a localStorage failure (e.g. quota exceeded,
		// or access denied in some private-browsing/locked-down browser configurations) block app
		// bootstrap. Worst case, the legacy keys are left unmigrated and get picked up next visit.
		console.warn('[ag-grid migration] failed, skipping', error);
	}
}

function runMigration(storage: Storage): void {
	if (storage.getItem(AG_GRID_MIGRATION_MARKER)) {
		return;
	}

	const allKeys = new Set<string>();
	for (let i = 0; i < storage.length; i++) {
		const key = storage.key(i);
		if (key !== null && key !== undefined) {
			allKeys.add(key);
		}
	}

	const skipped: string[] = [];

	allKeys.forEach((key) => {
		// Already migrated, or is the group-state half of a pair (handled via its base key below).
		if (key.startsWith(`${APP_STORAGE_PREFIX}:`) || key.endsWith(LEGACY_GROUP_STATE_SUFFIX)) {
			return;
		}
		if (IGNORED_PREFIXES.some((prefix) => key.startsWith(prefix))) {
			return;
		}

		const heading = key;
		const groupStateKey = `${heading}${LEGACY_GROUP_STATE_SUFFIX}`;
		if (!allKeys.has(groupStateKey)) {
			skipped.push(key);
			return;
		}

		const value = storage.getItem(key);
		const groupValue = storage.getItem(groupStateKey);

		if (value === null || groupValue === null) {
			return;
		}

		if (!looksLikeGridState(value) || !looksLikeGridState(groupValue)) {
			skipped.push(key, groupStateKey);
			return;
		}

		storage.setItem(buildNewColumnStateKey(heading), value);
		storage.setItem(buildNewGroupStateKey(heading), groupValue);
		storage.removeItem(key);
		storage.removeItem(groupStateKey);
	});

	if (skipped.length) {
		// eslint-disable-next-line no-console
		console.info('[ag-grid migration] left untouched (no matching pair or unexpected shape):', skipped);
	}

	storage.setItem(AG_GRID_MIGRATION_MARKER, 'true');
}
